import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, has, label } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";

const KINDS = [["feedback", "General feedback"], ["program_suggestion", "Suggest a pastoral program"], ["community_concern", "Share a community concern"]];
const STATUSES = ["new", "reviewed", "forwarded", "closed"];

const List = ({ items, gkkName, review, onStatus, testId }) => (
  <div className="space-y-3" data-testid={testId}>
    {items.map((f) => (
      <div key={f.feedback_id} className="rounded-xl border border-amber-700/15 bg-white p-4" data-testid={`feedback-item-${f.feedback_id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-stone-500">
          <span>{label(f.kind)} · {gkkName(f.gkk_id)} · {new Date(f.created_at).toLocaleDateString()}</span>
          {review ? (
            <Select value={f.status} onValueChange={(v) => onStatus(f, v)}>
              <SelectTrigger className="h-8 w-32 bg-white" data-testid={`feedback-status-${f.feedback_id}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
            </Select>
          ) : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-900">{label(f.status)}</span>}
        </div>
        <p className="mt-2 text-sm text-stone-800 whitespace-pre-wrap">{f.message}</p>
      </div>
    ))}
    {!items.length && <p className="text-sm text-stone-500">Nothing here yet.</p>}
  </div>
);

export default function Feedback() {
  const { user } = useAuth();
  const review = has(user, "feedback:review");
  const [gkks, setGkks] = useState([]);
  const [form, setForm] = useState({ kind: "feedback", message: "", gkk_id: "" });
  const [items, setItems] = useState([]);
  const load = () => api.get(review ? "/feedback" : "/feedback/mine").then((r) => setItems(r.data));
  useEffect(() => { api.get("/gkks").then((r) => setGkks(r.data)); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const gkkName = (id) => gkks.find((g) => g.gkk_id === id)?.name || "No GKK given";

  const submit = async () => {
    try {
      await api.post("/feedback", { ...form, gkk_id: form.gkk_id || null });
      toast.success("Thank you. Your message was received."); setForm({ ...form, message: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onStatus = async (f, status) => { try { await api.patch(`/feedback/${f.feedback_id}`, { status }); toast.success("Status updated"); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <PageHeader eyebrow="Your voice" title="Feedback & Suggestions" testId="feedback-header">
          Voluntary. Please do not include names, health details or private matters about other people. For confession or private spiritual counsel, please speak with a priest directly.
        </PageHeader>
        <div className="space-y-3 rounded-xl border border-amber-700/15 bg-white p-5">
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger className="bg-white" data-testid="feedback-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.gkk_id || "none"} onValueChange={(v) => setForm({ ...form, gkk_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-white" data-testid="feedback-gkk-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="none">My GKK (optional)</SelectItem>{gkks.map((g) => <SelectItem key={g.gkk_id} value={g.gkk_id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={6} placeholder="Write your message…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="feedback-message-input" />
          <Button onClick={submit} disabled={form.message.trim().length < 5} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="feedback-submit-button">Send</Button>
        </div>
      </div>
      <div>
        <h2 className="mb-4 mt-2 font-serif-display text-2xl font-semibold text-amber-900">{review ? "Messages received (author not shown)" : "My messages"}</h2>
        <List items={items} gkkName={gkkName} review={review} onStatus={onStatus} testId="feedback-list" />
      </div>
    </div>
  );
}
